import { AlpineComponent } from "alpinejs";
import { Canvg } from "canvg";
import type { jsPDF } from "jspdf";
import mpLogoSvg from "../../public/icons/mp-industri-text-logo-blaa-rgb.svg?raw";
import mpLogoIconSvg from "../../public/icons/mp-logo-cirkel-blaa.svg?raw";
import reducedIcon from "../../public/icons/reduced.jpg";
import { useTranslations } from "../hooks/useTranslations";
import { ProductViewModel } from "../types/components/ProductViewModel.ts";
import { BlockContainer } from "./pdf-generator/elements/blockContainer.ts";
import { BlockInlineContainer } from "./pdf-generator/elements/blockInlineContainer.ts";
import { Empty } from "./pdf-generator/elements/empty.ts";
import { FixedContainer } from "./pdf-generator/elements/fixedContainer.ts";
import { FlexContainer } from "./pdf-generator/elements/flexContainer.ts";
import { GridContainer } from "./pdf-generator/elements/gridContainer.ts";
import { ImagePdf } from "./pdf-generator/elements/imagePdf.ts";
import { Line } from "./pdf-generator/elements/line.ts";
import { PageContainer } from "./pdf-generator/elements/pageContainer.ts";
import { RichText2D } from "./pdf-generator/elements/richText2d.ts";
import { Svg } from "./pdf-generator/elements/svg.ts";
import { Text2D } from "./pdf-generator/elements/text2d.ts";
import { Box } from "./pdf-generator/layout/box.ts";
import { Margin } from "./pdf-generator/layout/margin.ts";
import { Position } from "./pdf-generator/layout/position.ts";
import { Size } from "./pdf-generator/layout/size.ts";

// For the UMD build: window.jspdf.jsPDF

type PdfProductSheetComponent = AlpineComponent<{
	busy: boolean;
	generate: () => Promise<void>;
	init: () => void;
}>;

export default (product: ProductViewModel): PdfProductSheetComponent => {
	const fontFamily = "Roboto";
	const primaryColor = "#1a1919";
	const secondaryColor = "#646363";
	const headerSize = 24;
	const textSize = 11.34;
	const smallTextSize = 8.5;
	const fontWeightNormal = "normal";
	const fontWeightBold = "bold";
	const lineHeight = 1.28;
	const t = useTranslations();

	const toSvgCanvas = async (svgString: string) => {
		const doc = new DOMParser().parseFromString(svgString, "image/svg+xml");
		const svgElement = doc.documentElement;

		let width: number | null;
		let height: number | null;

		const widthAttribute = svgElement.getAttribute("width");
		const heightAttribute = svgElement.getAttribute("height");
		const viewBoxAttribute = svgElement.getAttribute("viewBox");

		const toPx = (value: string | null) => {
			if (!value) {
				return null;
			}
			// crude: assume px if unitless; strip 'px'
			const m = /^([\d.]+)(px)?$/i.exec(value.trim());
			return m ? Number.parseFloat(m[1]) : null;
		};

		width = toPx(widthAttribute);
		height = toPx(heightAttribute);

		if ((!width || !height) && viewBoxAttribute) {
			const [, , viewBoxWidth, viewBoxHeight] = viewBoxAttribute.split(/\s+/).map(Number);
			width = width ?? viewBoxWidth;
			height = height ?? viewBoxHeight;
		}

		if (!width || !height) {
			width = 512;
			height = 512;
		}

		const canvas = document.createElement("canvas");
		canvas.width = Math.ceil(width);
		canvas.height = Math.ceil(height);

		const context = canvas.getContext("2d");
		if (!context) {
			throw new Error("Failed to get 2D context");
		}

		const canvg = Canvg.fromString(context, svgString, {
			ignoreMouse: true,
			ignoreAnimation: true,
			ignoreDimensions: false,
			scaleWidth: canvas.width,
			scaleHeight: canvas.height,
		});

		await canvg.render();
		return canvas;
	};

	const loadFontFromUrl = async (doc: jsPDF, url: string, fontName: string, style: string = fontWeightNormal) => {
		// 1. Fetch as ArrayBuffer
		const res = await fetch(url);
		if (!res.ok) {
			throw new Error(`Failed to load font from ${url}: ${res.status} ${res.statusText}`);
		}
		const buffer = await res.arrayBuffer();

		// 2. Convert to base64
		const uint8 = new Uint8Array(buffer);
		let binary = "";
		for (const element of uint8) {
			binary += String.fromCodePoint(element);
		}
		const base64 = btoa(binary);

		// 3. Register with jsPDF
		const fileName = `${fontName}-${style}.ttf`; // virtual name in VFS
		doc.addFileToVFS(fileName, base64);
		doc.addFont(fileName, fontName, style);
	};

	const addFooterAndHeader = async (pdfDocument: jsPDF) => {
		const page = {
			width: pdfDocument.internal.pageSize.getWidth(),
			height: pdfDocument.internal.pageSize.getHeight(),
		};

		const root = new FixedContainer(new Box(new Position(0, page.height - 20), new Size()), "footer-fixed-container");

		const headerLogoCanvas = await toSvgCanvas(mpLogoSvg);
		const headerLogo = new Svg(new Box(new Position(), new Size(70, 0)), "logo-1", headerLogoCanvas);
		const headerLogoContainer = new BlockInlineContainer(new Box(new Position(), new Size(70, 0)), "logo-container-1");
		headerLogoContainer.addElement(headerLogo);
		const headerLogoFixedContainer = new FixedContainer(
			new Box(new Position(20, 5), new Size(70, 0)),
			"logo-fixed-container"
		);
		headerLogoFixedContainer.addElement(headerLogoContainer);

		root.addElement(headerLogoFixedContainer);

		const footerLogoCanvas = await toSvgCanvas(mpLogoIconSvg);
		const footerLogo = new Svg(new Box(new Position(), new Size(10, 0)), "footer-logo", footerLogoCanvas);
		const footerLogoBlockContainer = new BlockInlineContainer(
			new Box(new Position(), new Size(10, 0), new Margin(20, 5, 0, 0)),
			"footer-block-container"
		);
		footerLogoBlockContainer.addElement(footerLogo);
		root.addElement(footerLogoBlockContainer);

		const footerGridContainer = new GridContainer(
			new Box(new Position(), new Size(page.width, 0), new Margin(50, 4, 3, 0)),
			"footer-grid-container",
			3,
			5
		);
		const footerTextMargin = new Margin(0, 0, 0, 0);

		const footerAddress = new Text2D(
			new Box(new Position(), new Size(), footerTextMargin),
			"footer-address",
			fontFamily,
			fontWeightNormal,
			smallTextSize,
			t.pdfAddress,
			primaryColor
		);

		footerGridContainer.addElement(footerAddress);

		const footerPhone = new Text2D(
			new Box(new Position(), new Size(), new Margin(8, 0, 0, 0)),
			"footer-phone",
			fontFamily,
			fontWeightNormal,
			smallTextSize,
			t.pdfPhone,
			primaryColor
		);

		footerGridContainer.addElement(footerPhone);

		const footerEmail = new Text2D(
			new Box(new Position(), new Size(), footerTextMargin),
			"footer-email",
			fontFamily,
			fontWeightNormal,
			smallTextSize,
			t.pdfEmail,
			primaryColor
		);

		footerGridContainer.addElement(footerEmail);

		root.addElement(footerGridContainer);

		await root.calculate(pdfDocument);
		await root.draw(pdfDocument);
	};

	return {
		busy: false,
		init() {
			const params = new URLSearchParams(window.location.search);
			if (params.get("autogenerate") === "true") {
				void this.generate();
			}
		},
		async generate() {
			const { jsPDF } = await import("jspdf");
			this.busy = true;
			try {
				const pdfDocument = new jsPDF({ unit: "mm", format: "a4", compress: true });

				await loadFontFromUrl(pdfDocument, "/_esfn/fonts/Roboto-Regular.ttf", "Roboto", fontWeightNormal);
				await loadFontFromUrl(pdfDocument, "/_esfn/fonts/Roboto-Bold.ttf", "Roboto", fontWeightBold);

				const page = {
					width: pdfDocument.internal.pageSize.getWidth(),
					height: pdfDocument.internal.pageSize.getHeight(),
				};

				const page1 = new PageContainer(
					new Box(new Position(0, 0), new Size(page.width, page.height), new Margin(20, 50, 20, 20)),
					"page-1"
				);

				page1.addFooter = addFooterAndHeader;

				const mainImage = new ImagePdf(
					new Box(new Position(), new Size(page.width, 0)),
					"main-image-1",
					product.images[0].url
				);

				page1.addElement(mainImage);

				const header = new Text2D(
					new Box(new Position(0, 0), new Size(0, 0), new Margin(0, 0, 0, 5)),
					"header-1",
					fontFamily,
					fontWeightNormal,
					headerSize,
					product.name,
					primaryColor,
					false,
					lineHeight
				);

				page1.addElement(header);

				if (product.corrosionClass.name !== "" || product.reducedTag == "True") {
					const flexContainer = new FlexContainer(
						new Box(new Position(), new Size(), new Margin(0, 0, 0, 8)),
						"corrosion-class-flex-container-1",
						3
					);

					if (product.corrosionClass.name !== "") {
						const corrosionClassText = new Text2D(
							new Box(new Position(0, 0), new Size(14, 7), new Margin(0, 0, 0, 0), product.corrosionClass.color),
							"corrosion-class-1",
							fontFamily,
							fontWeightBold,
							15,
							product.corrosionClass.name,
							"#FFFFFF",
							true
						);

						flexContainer.addElement(corrosionClassText);
					}

					if (product.reducedTag == "True") {
						const reducedTag = new ImagePdf(
							new Box(new Position(), new Size(0, 7), new Margin(0, 0, 0, 0)),
							"reduced-tag-1",
							reducedIcon
						);

						flexContainer.addElement(reducedTag);
					}

					page1.addElement(flexContainer);
				}

				const eNumberText = new Text2D(
					new Box(new Position(0, 0), new Size(0, 0), new Margin(0, 0, 0, 8)),
					"e-number-1",
					fontFamily,
					fontWeightNormal,
					textSize,
					`${t.pdfENumber}: ${product.eNumber}        |        ${t.pdfMPNumber}: ${product.partNo}`,
					primaryColor
				);
				page1.addElement(eNumberText);

				const descriptionContainer = new BlockContainer(new Box(), "description-container-1");

				const text = new RichText2D(
					new Box(new Position(0, 0), new Size(0, 0)),
					"description-1",
					fontFamily,
					fontWeightNormal,
					textSize,
					product.description,
					primaryColor
				);
				descriptionContainer.addElement(text);
				page1.addElement(descriptionContainer);

				await page1.calculate(pdfDocument);
				await page1.draw(pdfDocument);

				pdfDocument.addPage();
				const page2 = new PageContainer(
					new Box(new Position(0, 0), new Size(page.width, page.height), new Margin(20, 50, 20, 20)),
					"page-2"
				);

				page2.addFooter = addFooterAndHeader;

				const topLine = new Line(
					new Box(new Position(), new Size(0, 0.25), new Margin(0, 2, 0, 3)),
					"top-line",
					secondaryColor
				);
				const productDetailsHeader = new Text2D(
					new Box(),
					"product-details-header",
					fontFamily,
					fontWeightNormal,
					16,
					t.pdfProductDetails,
					primaryColor
				);
				const bottomLine = new Line(
					new Box(new Position(), new Size(0, 0.25), new Margin(0, 3, 0, 6)),
					"bottom-line",
					secondaryColor
				);
				page2.addElement(topLine);
				page2.addElement(productDetailsHeader);
				page2.addElement(bottomLine);
				const detailsContainer = new GridContainer(new Box(), "details-container-1", 2, 20, 5);
				detailsContainer.colSpan = 2;
				const parentDetailsContainer = new GridContainer(new Box(), "parent-details-container-1", 3);

				parentDetailsContainer.addElement(detailsContainer);
				parentDetailsContainer.addElement(new Empty());

				for (const [key, value] of Object.entries(product.productDetails)) {
					const keyText = new Text2D(
						new Box(new Position(), new Size(0, 0)),
						`detail-key-${key}`,
						fontFamily,
						fontWeightNormal,
						textSize,
						key,
						primaryColor
					);
					const valueText = new Text2D(
						new Box(new Position(), new Size(0, 0)),
						`detail-value-${key}`,
						fontFamily,
						fontWeightNormal,
						textSize,
						value,
						primaryColor
					);
					detailsContainer.addElement(keyText);
					detailsContainer.addElement(valueText);
				}
				page2.addElement(parentDetailsContainer);

				await page2.calculate(pdfDocument);
				await page2.draw(pdfDocument);

				const additionalImages = product.images.slice(1);

				if (additionalImages.length > 0) {
					pdfDocument.addPage();
					const page3 = new PageContainer(
						new Box(new Position(0, 0), new Size(page.width, page.height), new Margin(20, 50, 20, 20)),
						"page-3"
					);

					page3.addFooter = addFooterAndHeader;

					for (const image of additionalImages) {
						const productImage = new ImagePdf(
							new Box(new Position(), new Size(page.width, 0)),
							"additional-image",
							image.url
						);
						page3.addElement(productImage);
					}

					await page3.calculate(pdfDocument);
					await page3.draw(pdfDocument);
				}

				pdfDocument.save(`${product.name} - Datablad.pdf`);

				window.dataLayer = window.dataLayer || [];
				window.dataLayer.push({ event: "download_product_data_sheet" });

				// // For development purposes only
				// const blob = pdfDocument.output("blob");
				// const url = URL.createObjectURL(blob);
				//
				// // Create a temporary link and click it
				// const link = document.createElement("a");
				// link.href = url;
				// link.target = "_blank";
				// link.rel = "noopener noreferrer";
				// link.style.display = "none";
				// document.body.appendChild(link);
				// link.click();
				// link.remove()
				//
				// // Cleanup
				// setTimeout(() => URL.revokeObjectURL(url), 600_000);
			} catch (e) {
				console.error(e);
				alert("PDF generation failed.");
			} finally {
				this.busy = false;
			}
		},
	};
};
